import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Combobox } from '@shared/components/ui/AdvancedFilter/components';
import { Button } from '@shared/components/ui/button';
import { useCountries } from '@shared/hooks/useCountries';
import { CUSTOMER_ORIGIN_VALUES } from '../../constants';

interface CustomerFiltersPanelProps {
  ordersMinMonth: number | null;
  ordersMaxMonth: number | null;
  saleType: 'cod' | 'card' | null;
  countryId: number | null;
  customerOrigins: string[];
  activeFiltersCount: number;
  updateFilters: (filters: {
    ordersMinMonth?: number | null;
    ordersMaxMonth?: number | null;
    saleType?: 'cod' | 'card' | null;
    countryId?: number | null;
    customerOrigins?: string[];
  }) => void;
  onClear: () => void;
}

const comboboxButtonClass = 'h-[38px] text-sm border-border bg-background';

const FilterField = ({ label, children }: { label: string; children: ReactNode }) => (
  <div className="space-y-2">
    <label className="text-xs font-medium text-muted-foreground">{label}</label>
    {children}
  </div>
);

export const CustomerFiltersPanel = ({
  ordersMinMonth,
  ordersMaxMonth,
  saleType,
  countryId,
  customerOrigins,
  activeFiltersCount,
  updateFilters,
  onClear,
}: CustomerFiltersPanelProps) => {
  const { t } = useTranslation(['marketingModule', 'createEditClientsModule']);
  const { countryOptions, loading: countriesLoading } = useCountries();

  const ordersOptions = useMemo(
    () => [
      { label: t('filters.all', 'Todos'), value: '__all__' },
      { label: '0 pedidos', value: 'zero' },
      { label: '1–500 pedidos', value: 's_1_500' },
      { label: '501–2.000 pedidos', value: 's_501_2000' },
      { label: '2.001–5.000 pedidos', value: 's_2001_5000' },
      { label: '≥ 5.001 pedidos', value: 's_gt_5001' },
    ],
    [t],
  );

  const saleTypeOptions = useMemo(
    () => [
      { label: t('filters.all', 'Todos'), value: '__all__' },
      { label: 'COD', value: 'COD' },
      { label: t('customerFilters.saleTypeOptions.card', 'Tarjeta'), value: 'card' },
    ],
    [t],
  );

  const countryComboboxOptions = useMemo(
    () => [
      { label: t('filters.all', 'Todos'), value: '__all__' },
      ...countryOptions.map((country) => ({
        label: country.name,
        value: country.id,
      })),
    ],
    [countryOptions, t],
  );

  const sourceOptions = useMemo(
    () => [
      { label: t('filters.allSources', 'Todas'), value: '__all__' },
      ...CUSTOMER_ORIGIN_VALUES.map((origin) => ({
        label: t('createEditClientsModule:customerOrigins.' + origin, origin),
        value: origin,
      })),
    ],
    [t],
  );

  const getOrdersValue = () => {
    if (ordersMinMonth === null && ordersMaxMonth === null) return '__all__';
    if (ordersMinMonth === 0 && ordersMaxMonth === 0) return 'zero';
    if (ordersMinMonth === 1 && ordersMaxMonth === 500) return 's_1_500';
    if (ordersMinMonth === 501 && ordersMaxMonth === 2000) return 's_501_2000';
    if (ordersMinMonth === 2001 && ordersMaxMonth === 5000) return 's_2001_5000';
    if (ordersMinMonth === 5001) return 's_gt_5001';
    return '__all__';
  };

  const getSaleTypeValue = () => {
    if (saleType === null) return '__all__';
    if (saleType === 'cod') return 'COD';
    if (saleType === 'card') return 'card';
    return '__all__';
  };

  const getCountryValue = () => {
    if (countryId === null) return '__all__';
    return String(countryId);
  };

  const getSourceValue = () => {
    if (!customerOrigins || customerOrigins.length === 0) return '__all__';
    if (customerOrigins.length === 1) return customerOrigins[0];
    return '__all__';
  };

  const handleOrdersChange = (value: string) => {
    switch (value) {
      case '__all__':
        updateFilters({ ordersMinMonth: null, ordersMaxMonth: null });
        break;
      case 'zero':
        updateFilters({ ordersMinMonth: 0, ordersMaxMonth: 0 });
        break;
      case 's_1_500':
        updateFilters({ ordersMinMonth: 1, ordersMaxMonth: 500 });
        break;
      case 's_501_2000':
        updateFilters({ ordersMinMonth: 501, ordersMaxMonth: 2000 });
        break;
      case 's_2001_5000':
        updateFilters({ ordersMinMonth: 2001, ordersMaxMonth: 5000 });
        break;
      case 's_gt_5001':
        updateFilters({ ordersMinMonth: 5001, ordersMaxMonth: null });
        break;
    }
  };

  const handleSaleTypeChange = (value: string) => {
    switch (value) {
      case '__all__':
        updateFilters({ saleType: null });
        break;
      case 'COD':
        updateFilters({ saleType: 'cod' });
        break;
      case 'card':
        updateFilters({ saleType: 'card' });
        break;
    }
  };

  const handleCountryChange = (value: string) => {
    if (value === '__all__') {
      updateFilters({ countryId: null });
    } else {
      updateFilters({ countryId: parseInt(value, 10) });
    }
  };

  const handleSourceChange = (value: string) => {
    if (value === '__all__') {
      updateFilters({ customerOrigins: [] });
    } else {
      updateFilters({ customerOrigins: [value] });
    }
  };

  const notFoundLabel = t('filters.notFound', 'Sin resultados');

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <FilterField label={t('filters.orders', 'Pedidos (últ. mes)')}>
          <Combobox
            options={ordersOptions}
            value={getOrdersValue()}
            setValue={handleOrdersChange}
            placeholder={t('filters.all', 'Todos')}
            buttonClassName={comboboxButtonClass}
            dataTestId="marketing-filter-orders"
            translations={{ notFound: notFoundLabel }}
          />
        </FilterField>

        <FilterField label={t('filters.saleType', 'Tipo de venta')}>
          <Combobox
            options={saleTypeOptions}
            value={getSaleTypeValue()}
            setValue={handleSaleTypeChange}
            placeholder={t('filters.all', 'Todos')}
            buttonClassName={comboboxButtonClass}
            dataTestId="marketing-filter-sale-type"
            translations={{ notFound: notFoundLabel }}
          />
        </FilterField>

        <FilterField label={t('filters.country', 'País de envío')}>
          <Combobox
            options={countryComboboxOptions}
            value={getCountryValue()}
            setValue={handleCountryChange}
            placeholder={countriesLoading ? '...' : t('filters.all', 'Todos')}
            disabled={countriesLoading}
            buttonClassName={comboboxButtonClass}
            dataTestId="marketing-filter-country"
            translations={{ notFound: notFoundLabel }}
          />
        </FilterField>

        <FilterField label={t('filters.source', 'Fuente')}>
          <Combobox
            options={sourceOptions}
            value={getSourceValue()}
            setValue={handleSourceChange}
            placeholder={t('filters.allSources', 'Todas')}
            buttonClassName={comboboxButtonClass}
            dataTestId="marketing-filter-source"
            translations={{ notFound: notFoundLabel }}
          />
        </FilterField>
      </div>

      {activeFiltersCount > 0 && (
        <div className="flex items-center justify-end pt-3 border-t border-card-border">
          <Button
            variant="ghost"
            size="sm"
            className="text-sm text-muted-foreground hover:text-foreground"
            onClick={onClear}
            type="button"
            data-testid="marketing-customer-filters-clear"
          >
            {t('filters.clear', 'Limpiar filtros')} ({activeFiltersCount})
          </Button>
        </div>
      )}
    </div>
  );
};
